import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import jwt_decode from 'jwt-decode';
import { ToastrService } from 'ngx-toastr';
import { map, switchMap, take, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { TenantInnerService } from '../_services/tenant-inner.service';
import { TokenService } from '../_services/token.service';
import { ADMIN_ROLE, Feature } from './Constants';

export function createFeatureGuard(...requiredFeatures: Feature[]): CanActivateFn {
  return (route, state) => {
    const tokenService = inject(TokenService);
    const tenantInnerService = inject(TenantInnerService);
    const toastr = inject(ToastrService);
    const router = inject(Router);

    const token = tokenService.getToken();
    if (token === null) {
      localStorage.setItem('redirectUrl', state.url);
      router.navigateByUrl('/');
      return false;
    }

    // Admins skip the feature check below, but they still need the tenantId override applied -
    // otherwise an admin clicking a cross-tenant link (e.g. a mention email) would keep seeing
    // whatever tenant was active before, everywhere except this one page's own data fetch.
    let isAdmin = false;
    try {
      const decoded = jwt_decode<Record<string, string[]>>(token);
      isAdmin = decoded[environment.roleKey]?.includes(ADMIN_ROLE) ?? false;
    } catch {
      // malformed token — fall through to feature check
    }

    // A link (e.g. from an email) can carry the tenant it was generated for. ensureUserTenant()
    // restores the saved/session tenant on a cold load (direct URL navigation, refresh) *before*
    // we look at that override below, and resolves immediately - no network call - once a tenant
    // is already active, so it's safe to call unconditionally instead of racing setUserTenant().
    const urlTenantId = route.queryParamMap.get('tenantId');

    return tenantInnerService.ensureUserTenant().pipe(
      take(1),
      tap(tenants => {
        if (!urlTenantId || urlTenantId.toLowerCase() === tokenService.GetTenantId()?.toLowerCase()) {
          return;
        }
        const targetTenant = tenants.find(t => t.id.toLowerCase() === urlTenantId.toLowerCase());
        if (targetTenant) {
          tenantInnerService.setTenant(targetTenant);
          toastr.info(`Zu Mandant "${targetTenant.displayName ?? targetTenant.name}" gewechselt, um den verlinkten Eintrag anzuzeigen.`);
        } else if (tenants.length > 0) {
          // Tenant list loaded successfully but the linked tenant isn't in it - the user has no
          // access to it. Fall through with the currently active tenant instead of silently
          // failing the feature check below with no explanation.
          toastr.warning('Der Link gehört zu einem Mandanten, auf den Sie keinen Zugriff haben.');
        }
      }),
      switchMap(() => tenantInnerService.curTenant$.pipe(take(1))),
      map(tenant => {
        if (isAdmin) {
          return true;
        }
        const features = tenant?.features;
        if (features && requiredFeatures.some(f => features.includes(f))) {
          return true;
        }
        localStorage.setItem('redirectUrl', state.url);
        router.navigateByUrl('/');
        return false;
      })
    );
  };
}
